import type { SupabaseClient } from "@supabase/supabase-js";
import { normalizeEmail, type AuthAccount } from "../domain/auth";
import {
  resolvedLoginFromRow,
  type ResolvedLogin,
} from "../domain/guardianLogin";
import {
  formatDateLabel,
  sessionKey,
  type EventAttendanceSession,
  type LiftEvent,
  type Registration,
  type Student,
} from "../domain/data";
import type { MailKind, MailMessage, NotificationSettings } from "../domain/mail";
import { browserSupabase } from "../domain/supabaseBrowser";
import type { FormQuestion } from "../domain/questions";
import {
  accountFromProfile,
  eventFromRow,
  eventToRow,
  isRecord,
  isUuid,
  parseEventRow,
  parseRegistrationRow,
  parseStudentRow,
  questionsFromRows,
  registrationFromRow,
  studentFromRow,
  studentToRow,
  text,
  type ProfileRole,
} from "./map";

export type RemoteCounts = Record<
  string,
  { registered: number; waitlisted: number; checkedIn: number; cancelled: number }
>;

export type EventsBundle = {
  events: LiftEvent[];
  registrations: Record<string, Registration[]>;
  sessions: Record<string, EventAttendanceSession>;
  counts: RemoteCounts;
};

export type RemoteForm = {
  id: string;
  year: string;
  name: string;
  status: "open" | "archived";
  copy: unknown;
  createdAt: string;
  updatedAt: string;
};

export type RemoteSubmission = {
  id: string;
  formId: string;
  studentId: string;
  submittedAt: string;
  answers: unknown;
};

export function remoteClient(): SupabaseClient | null {
  return browserSupabase();
}

function fail(error: { message: string } | null): void {
  if (error) throw new Error(error.message);
}

/** Anon can read open events, but not every related table. A denied side query must not hide the list. */
function optionalRows<T>(
  result: { data: T[] | null; error: { message: string; code?: string } | null },
): T[] {
  if (!result.error) return result.data ?? [];
  const denied =
    result.error.code === "42501" || /permission denied/i.test(result.error.message);
  if (denied) return [];
  throw new Error(result.error.message);
}

function list(value: unknown): unknown[] {
  return Array.isArray(value) ? value : [];
}

function count(value: unknown): number {
  return typeof value === "number" ? value : 0;
}

export async function loadEventsBundle(client: SupabaseClient): Promise<EventsBundle> {
  const [eventsResult, questionsResult, registrationsResult, sessionsResult, marksResult, countsResult] =
    await Promise.all([
      client.from("events").select("*"),
      client.from("event_questions").select("*").order("position"),
      client.from("registrations").select("*"),
      client.from("attendance_sessions").select("*"),
      client.from("attendance_marks").select("*"),
      client.rpc("event_registration_counts"),
    ]);
  fail(eventsResult.error);
  fail(questionsResult.error);
  fail(countsResult.error);
  const registrationRows = optionalRows(registrationsResult);
  const sessionRows = optionalRows(sessionsResult);
  const markRows = optionalRows(marksResult);

  const questionsByEvent = new Map<string, unknown[]>();
  list(questionsResult.data).forEach((item) => {
    if (!isRecord(item) || typeof item.event_id !== "string") return;
    const group = questionsByEvent.get(item.event_id) ?? [];
    group.push(item);
    questionsByEvent.set(item.event_id, group);
  });

  const events = list(eventsResult.data).flatMap((item) => {
    const row = parseEventRow(item);
    if (!row) return [];
    const event = eventFromRow(row, questionsFromRows(questionsByEvent.get(row.id) ?? []));
    return event ? [event] : [];
  });

  const registrations: Record<string, Registration[]> = {};
  registrationRows.forEach((item) => {
    const row = parseRegistrationRow(item);
    const registration = row ? registrationFromRow(row) : null;
    if (!registration) return;
    const group = registrations[registration.eventId] ?? [];
    group.push(registration);
    registrations[registration.eventId] = group;
  });

  const marksBySession = new Map<string, EventAttendanceSession["rows"]>();
  markRows.forEach((item) => {
    if (!isRecord(item) || typeof item.session_id !== "string") return;
    const studentId = text(item.student_id);
    const mark = item.mark === "absent" ? "absent" : "present";
    if (!studentId) return;
    const group = marksBySession.get(item.session_id) ?? [];
    group.push({ studentId, status: mark, at: null });
    marksBySession.set(item.session_id, group);
  });

  const sessions: Record<string, EventAttendanceSession> = {};
  sessionRows.forEach((item) => {
    if (!isRecord(item)) return;
    const eventId = text(item.event_id);
    const dateIso = text(item.session_on).slice(0, 10);
    const id = text(item.id);
    if (!eventId || !dateIso) return;
    const key = sessionKey(eventId, dateIso);
    sessions[key] = {
      id: key,
      eventId,
      dateIso,
      dateLabel: formatDateLabel(dateIso),
      savedAt: text(item.saved_at) || dateIso,
      rows: marksBySession.get(id) ?? [],
    };
  });

  const counts: RemoteCounts = {};
  list(countsResult.data).forEach((item) => {
    if (!isRecord(item) || typeof item.event_id !== "string") return;
    counts[item.event_id] = {
      registered: count(item.registered),
      waitlisted: count(item.waitlisted),
      checkedIn: count(item.checked_in),
      cancelled: count(item.cancelled),
    };
  });

  return { events, registrations, sessions, counts };
}

export async function saveEvent(client: SupabaseClient, event: LiftEvent): Promise<void> {
  const { error } = await client.from("events").upsert(eventToRow(event));
  fail(error);
  await replaceEventQuestions(client, event.id, event.questions);
}

export async function replaceEventQuestions(
  client: SupabaseClient,
  eventId: string,
  questions: FormQuestion[],
): Promise<void> {
  const removed = await client.from("event_questions").delete().eq("event_id", eventId);
  fail(removed.error);
  if (questions.length === 0) return;
  const inserted = await client.from("event_questions").insert(
    questions.map((question, position) => ({
      event_id: eventId,
      id: question.id,
      position,
      label: question.label,
      label_es: question.labelEs,
      kind: question.kind,
      required: question.required,
      options: question.options,
      options_es: question.optionsEs,
    })),
  );
  fail(inserted.error);
}

export async function saveEventStatus(
  client: SupabaseClient,
  eventId: string,
  status: LiftEvent["status"],
): Promise<void> {
  const { error } = await client.from("events").update({ status }).eq("id", eventId);
  fail(error);
}

export async function saveRegistration(
  client: SupabaseClient,
  row: Registration,
  mode: "family" | "check-in" | "clear-check-in",
): Promise<Registration> {
  const payload: {
    id?: string;
    event_id: string;
    student_id: string;
    status: Registration["status"];
    registered_at: string;
    waitlisted_at: string | null;
    cancelled_at: string | null;
    answers: Record<string, string>;
    checked_in_at?: string | null;
    checked_in_by?: string | null;
  } = {
    event_id: row.eventId,
    student_id: row.studentId,
    status: row.status,
    registered_at: row.registeredAt,
    waitlisted_at: row.waitlistedAt,
    cancelled_at: row.cancelledAt,
    answers: row.answers,
  };
  if (isUuid(row.id)) payload.id = row.id;
  if (mode === "check-in") {
    const { data, error } = await client.auth.getUser();
    fail(error);
    payload.checked_in_at = new Date().toISOString();
    payload.checked_in_by = data.user?.id ?? null;
  } else if (mode === "clear-check-in") {
    payload.checked_in_at = null;
    payload.checked_in_by = null;
  } else if (!row.checkedInAt) {
    payload.checked_in_at = null;
    payload.checked_in_by = null;
  } else if (row.checkedInAt.includes("T")) {
    payload.checked_in_at = row.checkedInAt;
    payload.checked_in_by =
      row.checkedInBy && isUuid(row.checkedInBy) ? row.checkedInBy : null;
  }

  const { data, error } = await client
    .from("registrations")
    .upsert(payload, { onConflict: "event_id,student_id" })
    .select("*")
    .single();
  fail(error);
  const saved = parseRegistrationRow(data);
  const registration = saved ? registrationFromRow(saved) : null;
  if (!registration) throw new Error("Could not save that registration.");
  return registration;
}

export async function saveAttendance(
  client: SupabaseClient,
  session: EventAttendanceSession,
): Promise<void> {
  const { data: userData, error: userError } = await client.auth.getUser();
  fail(userError);
  const saved = await client
    .from("attendance_sessions")
    .upsert(
      {
        event_id: session.eventId,
        session_on: session.dateIso,
        taken_by: userData.user?.id ?? null,
        saved_at: new Date().toISOString(),
      },
      { onConflict: "event_id,session_on" },
    )
    .select("id")
    .single();
  fail(saved.error);
  const sessionId =
    isRecord(saved.data) && typeof saved.data.id === "string" ? saved.data.id : "";
  if (!isUuid(sessionId)) throw new Error("Could not save attendance.");
  const removed = await client.from("attendance_marks").delete().eq("session_id", sessionId);
  fail(removed.error);
  if (session.rows.length === 0) return;
  const inserted = await client.from("attendance_marks").insert(
    session.rows.map((row) => ({
      session_id: sessionId,
      student_id: row.studentId,
      mark: row.status,
    })),
  );
  fail(inserted.error);
}

export async function loadStudents(client: SupabaseClient): Promise<Student[]> {
  const { data, error } = await client.from("students").select("*").order("last_name");
  fail(error);
  return list(data).flatMap((item) => {
    const row = parseStudentRow(item);
    const student = row ? studentFromRow(row) : null;
    return student ? [student] : [];
  });
}

export async function ensureHouseholdId(client: SupabaseClient): Promise<string> {
  const { data, error } = await client.rpc("ensure_my_household");
  fail(error);
  if (typeof data !== "string" || !isUuid(data)) {
    throw new Error("Could not open the household.");
  }
  return data;
}

export async function updateExistingStudent(
  client: SupabaseClient,
  student: Student,
): Promise<void> {
  const { id: _id, household_id: _household, ...payload } = studentToRow(student, null);
  const { data, error } = await client
    .from("students")
    .update(payload)
    .eq("id", student.id)
    .select("id");
  fail(error);
  if (!Array.isArray(data) || data.length === 0) {
    throw new Error("Could not update that student.");
  }
}

export async function deleteStudentRecord(
  client: SupabaseClient,
  studentId: string,
): Promise<void> {
  const marks = await client.from("attendance_marks").delete().eq("student_id", studentId);
  fail(marks.error);
  const registrations = await client.from("registrations").delete().eq("student_id", studentId);
  fail(registrations.error);
  const submissions = await client.from("form_submissions").delete().eq("student_id", studentId);
  fail(submissions.error);
  const { data, error } = await client
    .from("students")
    .delete()
    .eq("id", studentId)
    .select("id");
  fail(error);
  if (!Array.isArray(data) || data.length === 0) {
    throw new Error("Could not remove that student.");
  }
}

export async function saveStudent(
  client: SupabaseClient,
  student: Student,
  householdId: string | null,
): Promise<void> {
  const row = studentToRow(student, householdId);
  if (!householdId) {
    const { error } = await client.from("students").insert(row);
    if (error && error.code !== "23505") fail(error);
    return;
  }
  const existing = await client.from("students").select("id").eq("id", student.id).maybeSingle();
  fail(existing.error);
  if (isRecord(existing.data)) {
    const { household_id: _household, ...rest } = row;
    const payload = householdId ? row : rest;
    const { error } = await client.from("students").update(payload).eq("id", student.id);
    fail(error);
    return;
  }
  const { error } = await client.from("students").insert(row);
  fail(error);
}

export async function loadForms(client: SupabaseClient): Promise<RemoteForm[]> {
  const { data, error } = await client
    .from("school_year_forms")
    .select("*")
    .order("school_year", { ascending: false });
  fail(error);
  return list(data).flatMap((item) => {
    if (!isRecord(item) || typeof item.id !== "string") return [];
    const status = item.status === "archived" ? "archived" : "open";
    return [
      {
        id: item.id,
        year: text(item.school_year),
        name: text(item.name),
        status,
        copy: item.copy,
        createdAt: text(item.created_at),
        updatedAt: text(item.updated_at),
      },
    ];
  });
}

export async function loadSubmissions(client: SupabaseClient): Promise<RemoteSubmission[]> {
  const { data, error } = await client.from("form_submissions").select("*");
  fail(error);
  return list(data).flatMap((item) => {
    if (!isRecord(item) || typeof item.id !== "string") return [];
    return [
      {
        id: item.id,
        formId: text(item.form_id),
        studentId: text(item.student_id),
        submittedAt: text(item.submitted_at),
        answers: item.answers,
      },
    ];
  });
}

export async function saveForm(
  client: SupabaseClient,
  form: { id: string; year: string; name: string; status: "open" | "archived"; copy: unknown },
): Promise<void> {
  const { error } = await client.from("school_year_forms").upsert({
    id: form.id,
    school_year: form.year,
    name: form.name,
    status: form.status,
    copy: form.copy,
  });
  fail(error);
}

export async function saveSubmission(
  client: SupabaseClient,
  row: RemoteSubmission,
): Promise<void> {
  const payload: {
    id?: string;
    form_id: string;
    student_id: string;
    submitted_at: string;
    answers: unknown;
  } = {
    form_id: row.formId,
    student_id: row.studentId,
    submitted_at: row.submittedAt,
    answers: row.answers,
  };
  if (isUuid(row.id)) payload.id = row.id;
  const { data: authData } = await client.auth.getSession();
  if (!authData.session) {
    const { error } = await client.from("form_submissions").insert(payload);
    if (error && error.code !== "23505") fail(error);
    return;
  }
  const { error } = await client
    .from("form_submissions")
    .upsert(payload, { onConflict: "form_id,student_id" });
  fail(error);
}

export async function loadMailMessages(client: SupabaseClient): Promise<MailMessage[]> {
  const { data, error } = await client
    .from("mail_messages")
    .select("*")
    .order("created_at", { ascending: false });
  fail(error);
  return list(data).flatMap((item) => {
    if (!isRecord(item) || typeof item.id !== "string") return [];
    const kind = mailKind(item.kind);
    if (!kind) return [];
    return [
      {
        id: item.id,
        kind,
        to: Array.isArray(item.to_addresses)
          ? item.to_addresses.filter((value): value is string => typeof value === "string")
          : [],
        from: text(item.from_address),
        subject: text(item.subject),
        body: text(item.body),
        createdAt: text(item.created_at),
        studentId: text(item.student_id),
        eventId: typeof item.event_id === "string" ? item.event_id : undefined,
      },
    ];
  });
}

export async function insertFamilyMail(
  client: SupabaseClient,
  message: MailMessage,
  eventId: string | null,
  delivery: "recorded" | "blocked" | "redirected" | "sent",
): Promise<string | null> {
  if (message.kind !== "family_welcome" && message.kind !== "event_confirmation") return null;
  if (!isUuid(message.studentId)) return null;
  const { data, error } = await client
    .from("mail_messages")
    .insert({
      kind: message.kind,
      to_addresses: message.to,
      from_address: message.from,
      subject: message.subject,
      body: message.body,
      student_id: message.studentId,
      event_id: eventId && isUuid(eventId) ? eventId : null,
      delivery,
    })
    .select("id")
    .single();
  fail(error);
  return isRecord(data) && typeof data.id === "string" ? data.id : null;
}

export type QueuedStaffMail = {
  id: string | null;
  to: string[];
};

export async function queueStaffMail(
  client: SupabaseClient,
  kind: "staff_registration" | "staff_rsvp",
  studentId: string,
  eventId: string | null,
  subject: string,
  body: string,
): Promise<QueuedStaffMail> {
  if (!isUuid(studentId)) return { id: null, to: [] };
  const { data, error } = await client.rpc("queue_staff_mail", {
    p_kind: kind,
    p_student_id: studentId,
    p_event_id: eventId && isUuid(eventId) ? eventId : null,
    p_subject: subject,
    p_body: body,
  });
  fail(error);
  if (!isRecord(data) || !Array.isArray(data.to)) return { id: null, to: [] };
  return {
    id: typeof data.id === "string" ? data.id : null,
    to: data.to.filter((item): item is string => typeof item === "string"),
  };
}

/** Asks the deliver-mail edge function to send one recorded row. Recipients are resolved on the server. */
export async function deliverHostedMail(
  client: SupabaseClient,
  messageId: string,
): Promise<{ delivered: boolean; reason: string }> {
  const { data, error } = await client.functions.invoke("deliver-mail", {
    body: { messageId },
  });
  if (error || !isRecord(data)) {
    return { delivered: false, reason: "provider-not-configured" };
  }
  return {
    delivered: data.delivered === true,
    reason: typeof data.reason === "string" ? data.reason : "recorded",
  };
}

export async function loadNotificationSettings(
  client: SupabaseClient,
): Promise<Partial<NotificationSettings> | null> {
  const { data, error } = await client
    .from("app_settings")
    .select("value")
    .eq("key", "notification_settings")
    .maybeSingle();
  if (error || !isRecord(data)) return null;
  return isRecord(data.value) ? settingsFromUnknown(data.value) : null;
}

export async function saveNotificationSettings(
  client: SupabaseClient,
  settings: NotificationSettings,
): Promise<void> {
  const { error } = await client.from("app_settings").upsert({
    key: "notification_settings",
    value: settings,
  });
  fail(error);
}

export async function loadHostedAccount(client: SupabaseClient): Promise<AuthAccount | null> {
  const { data, error } = await client.auth.getUser();
  fail(error);
  const user = data.user;
  if (!user?.email) return null;
  const profileResult = await client
    .from("profiles")
    .select("first_name,last_name,role,status,must_reset_password")
    .eq("id", user.id)
    .maybeSingle();
  const profileError = profileResult.error;
  const fallback =
    profileError && /must_reset_password/i.test(profileError.message)
      ? await client
          .from("profiles")
          .select("first_name,last_name,role,status")
          .eq("id", user.id)
          .maybeSingle()
      : null;
  if (fallback) fail(fallback.error);
  else fail(profileError);
  const rawProfile: unknown = fallback?.data ?? profileResult.data;
  if (!isRecord(rawProfile)) return null;
  const profile = rawProfile;
  if (profile.status === "inactive") {
    throw new Error("This account is inactive.");
  }
  const role = profileRole(profile.role);
  if (role === "parent") await ensureHouseholdId(client);
  const students = role === "staff" ? [] : await loadStudents(client);
  const own = await client.from("students").select("id").eq("profile_id", user.id).maybeSingle();
  fail(own.error);
  const ownStudentId =
    isRecord(own.data) && typeof own.data.id === "string" ? own.data.id : null;
  const meta = user.user_metadata ?? {};
  const phone =
    typeof meta.phone === "string"
      ? meta.phone
      : typeof meta.guardian_phone === "string"
        ? meta.guardian_phone
        : "";
  const pendingStudentIds = students
    .filter((student) => student.acceptance === "pending")
    .map((student) => student.id);
  const acceptedStudentIds = students
    .filter((student) => (student.acceptance ?? "accepted") === "accepted")
    .map((student) => student.id);
  return accountFromProfile({
    email: user.email,
    firstName: text(profile.first_name),
    lastName: text(profile.last_name),
    phone,
    role,
    studentIds: acceptedStudentIds,
    pendingStudentIds,
    mustResetPassword: profile.must_reset_password === true,
    ownStudentId,
  });
}

/** Staff lookup. The database returns role and dates, and nothing for anyone else. */
export async function loadGuardianLogin(
  client: SupabaseClient,
  email: string,
): Promise<ResolvedLogin | null> {
  const { data, error } = await client.rpc("guardian_login_status", {
    p_email: normalizeEmail(email),
  });
  if (error) throw new Error(error.message);
  const row = Array.isArray(data) ? data[0] : data;
  if (row == null) return null;
  const parsed = resolvedLoginFromRow(row);
  if (!parsed) throw new Error("Could not read that login.");
  return parsed;
}

export async function finishHostedParentClaim(
  client: SupabaseClient,
  input: {
    given: string;
    family: string;
    phone: string;
    password: string;
    mustResetPassword: boolean;
    decisions: Record<string, "accept" | "decline" | undefined>;
  },
): Promise<{ ok: true } | { ok: false; error: string }> {
  const data = {
    phone: input.phone.trim(),
    first_name: input.given.trim(),
    last_name: input.family.trim(),
  };
  const updated = input.mustResetPassword
    ? await client.auth.updateUser({ password: input.password, data })
    : await client.auth.updateUser({ data });
  if (updated.error) return { ok: false, error: updated.error.message };
  const decided: Record<string, "accept" | "decline"> = {};
  Object.entries(input.decisions).forEach(([id, decision]) => {
    if (decision === "accept" || decision === "decline") decided[id] = decision;
  });
  const { error } = await client.rpc("finish_parent_claim", {
    p_given: input.given.trim(),
    p_family: input.family.trim(),
    p_password: input.mustResetPassword ? input.password : "",
    p_decisions: decided,
  });
  if (error) return { ok: false, error: error.message };
  return { ok: true };
}

export function remoteSessionEvent(event: string): boolean {
  return event === "INITIAL_SESSION" || event === "SIGNED_IN" || event === "SIGNED_OUT";
}

/** Load remote data once the login session is known.
 *  The auth callback must not call Supabase itself; that waits on the session lock.
 */
export function onRemoteSession(client: SupabaseClient, load: () => void): () => void {
  const { data } = client.auth.onAuthStateChange((event) => {
    if (!remoteSessionEvent(event)) return;
    setTimeout(load, 0);
  });
  return () => data.subscription.unsubscribe();
}

export function subscribeChanges(
  client: SupabaseClient,
  tables: string[],
  onChange: () => void,
): () => void {
  const channel = client.channel(`lift-${tables.join("-")}-${crypto.randomUUID()}`);
  tables.forEach((table) => {
    channel.on(
      "postgres_changes",
      { event: "*", schema: "public", table },
      () => onChange(),
    );
  });
  channel.subscribe();
  return () => {
    void client.removeChannel(channel);
  };
}

function settingsFromUnknown(value: Record<string, unknown>): Partial<NotificationSettings> {
  const raw = isRecord(value.schoolEmails) ? value.schoolEmails : null;
  const settings: Partial<NotificationSettings> = {
    centralEmail: text(value.centralEmail),
    usePerSchool: value.usePerSchool === true,
  };
  if (!raw) return settings;
  settings.schoolEmails = {
    "Suttons Bay": text(raw["Suttons Bay"]),
    Northport: text(raw.Northport),
    Leland: text(raw.Leland),
    "Glen Lake": text(raw["Glen Lake"]),
  };
  return settings;
}

function profileRole(value: unknown): ProfileRole {
  if (value === "admin" || value === "staff" || value === "student") return value;
  return "parent";
}

function mailKind(value: unknown): MailKind | null {
  if (
    value === "family_welcome" ||
    value === "staff_registration" ||
    value === "event_confirmation" ||
    value === "staff_rsvp"
  ) {
    return value;
  }
  return null;
}
